import React, { useEffect, useState } from 'react';
import { Badge, Box, Button, Flex, Text, Tooltip } from '@chakra-ui/react';

import {
  BACKGROUND_SCANNER_BUFFER_MAX_LENGTH,
  BACKGROUND_SCANNER_INFINITY_INTERVAL,
  BACKGROUND_SCANNER_PREFIX,
  BACKGROUND_SCANNER_SUFFIX,
} from './scanners.consts';

export type BackgroundScannerProps = {
  onScan: (data: string) => void;
  onConnectionChange?: (connected: boolean) => void;
};

export default function BackgroundScanner({ onScan, onConnectionChange }: BackgroundScannerProps) {
  const [isConnected, setIsConnected] = useState(false);
  const [buffer, setBuffer] = useState('');
  const [hasFocus, setHasFocus] = useState(document.hasFocus());

  // Focus detection
  useEffect(() => {
    const handleFocus = () => setHasFocus(true);
    const handleBlur = () => setHasFocus(false);

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  // Buffer auto-clear timer
  useEffect(() => {
    if (!isConnected) return;

    // Set up a timer to periodically clear the buffer if it hasn't been used
    const bufferClearTimer = setInterval(() => {
      if (buffer.length > 0) {
        setBuffer('');
      }
    }, BACKGROUND_SCANNER_INFINITY_INTERVAL); // Clear buffer every 30 seconds of inactivity

    return () => {
      clearInterval(bufferClearTimer);
    };
  }, [isConnected, buffer]);

  // Handle keyboard events globally
  useEffect(() => {
    if (!isConnected) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip special keys like Shift, Control, Alt, etc.
      if (
        e.key === 'Shift' ||
        e.key === 'Control' ||
        e.key === 'Alt' ||
        e.key === 'Meta' ||
        e.key === 'CapsLock' ||
        e.key === 'Tab' ||
        e.key === 'ArrowUp' ||
        e.key === 'ArrowDown' ||
        e.key === 'ArrowLeft' ||
        e.key === 'ArrowRight' ||
        e.key === 'Home' ||
        e.key === 'End' ||
        e.key === 'PageUp' ||
        e.key === 'PageDown' ||
        e.key === 'Insert' ||
        e.key === 'Delete' ||
        e.key === 'Escape' ||
        e.key === 'F1' ||
        e.key === 'F2' ||
        e.key === 'F3' ||
        e.key === 'F4' ||
        e.key === 'F5' ||
        e.key === 'F6' ||
        e.key === 'F7' ||
        e.key === 'F8' ||
        e.key === 'F9' ||
        e.key === 'F10' ||
        e.key === 'F11' ||
        e.key === 'F12'
      ) {
        return;
      }

      // Add the key to the buffer
      setBuffer((prev) => {
        const newBuffer = prev + e.key;

        // Check if the buffer contains the complete pattern (prefix + data + suffix)
        if (newBuffer.includes(BACKGROUND_SCANNER_PREFIX) && newBuffer.includes(BACKGROUND_SCANNER_SUFFIX)) {
          const startIndex = newBuffer.lastIndexOf(BACKGROUND_SCANNER_PREFIX);
          const endIndex = newBuffer.lastIndexOf(BACKGROUND_SCANNER_SUFFIX);

          // Make sure the suffix comes after the prefix
          if (endIndex > startIndex) {
            // Extract the data between prefix and suffix
            const extractedData = newBuffer.substring(startIndex + BACKGROUND_SCANNER_PREFIX.length, endIndex);

            try {
              // Call the callback with the extracted data
              onScan(extractedData);
            } catch (error) {
              console.error('Error in onScan callback:', error);
            }

            // Clear the buffer after processing
            return '';
          }
        }

        // Prevent buffer from growing too large
        if (newBuffer.length > BACKGROUND_SCANNER_BUFFER_MAX_LENGTH) {
          return newBuffer.slice(-BACKGROUND_SCANNER_BUFFER_MAX_LENGTH);
        }

        return newBuffer;
      });
    };

    // Add event listener for keydown events
    window.addEventListener('keydown', handleKeyDown);

    // Notify parent component that scanner is connected
    onConnectionChange?.(true);

    // Clean up event listener when component unmounts or disconnects
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      onConnectionChange?.(false);
    };
  }, [isConnected, onScan, onConnectionChange]);

  const handleConnect = () => {
    setIsConnected(true);
    setBuffer('');
  };

  const handleDisconnect = () => {
    setIsConnected(false);
    setBuffer('');
  };

  return (
    <Box>
      {!isConnected ? (
        <Button colorScheme="blue" onClick={handleConnect}>
          Connect Background Scanner
        </Button>
      ) : (
        <Box>
          <Text mb={2} color="green.500">
            Connected to Background Scanner (Listening for keyboard input)
          </Text>

          <Flex mb={3} alignItems="center">
            <Badge colorScheme={hasFocus ? 'green' : 'red'} p={2} borderRadius="md" mr={2}>
              {hasFocus ? 'Browser has focus' : 'Browser needs focus'}
            </Badge>
            <Tooltip
              label="Background Scanner requires the browser window to have focus to capture keyboard input. Click anywhere on the page if scanning doesn't work."
              placement="top"
              hasArrow
            >
              <Text fontSize="sm" color={hasFocus ? 'green.500' : 'red.500'} fontStyle="italic">
                {hasFocus ? 'Ready to scan' : 'Click on the browser window to enable scanning'}
              </Text>
            </Tooltip>
          </Flex>

          <Text mb={3} fontSize="sm" color="grey.600">
            Note: Background Scanner must have focus on the browser window to work properly.
          </Text>

          <Button colorScheme="red" onClick={handleDisconnect}>
            Disconnect Background Scanner
          </Button>
        </Box>
      )}
    </Box>
  );
}
